import React from 'react';
import { Check } from 'lucide-react';

const ProcessTracker = ({ currentStatus, stage, layout = "two-line" }) => {
  const stages = [
    { id: 'expression_of_interest', label: 'Expression of Interest', short: 'Expression\nof Interest' },
    { id: 'ticket_blocking_paid', label: 'Ticket Blocking Payment', short: 'Ticket Blocking\nPayment' },
    { id: 'due_diligence', label: 'Due Diligence & Paperwork', short: 'Due Diligence\n& Paperwork' },
    { id: 'member_curation', label: 'Group Curated', short: 'Group\nCurated' },
    { id: 'spv_formed', label: 'SPV Formed', short: 'SPV\nFormed' },
    { id: 'stage1_processed', label: 'Stage 1 Paid', short: 'Stage 1\nPaid' },
    { id: 'asset_purchased', label: 'Asset Purchased', short: 'Asset\nPurchased' },
    { id: 'asset_deployed', label: 'Asset Deployed', short: 'Asset\nDeployed' }
  ];

  // Map stage to determine completed stages
  // Stage is now the single source of truth for progress tracking
  const getCompletedStages = (status, stage) => {
    const completed = new Set();
    
    // Map stage values to process tracker stages
    const stageMapping = {
      'stage_1_booking': ['expression_of_interest'],
      'stage_2_due_diligence': ['expression_of_interest', 'ticket_blocking_paid'],
      'stage_3_group_curated': ['expression_of_interest', 'ticket_blocking_paid', 'due_diligence'],
      'stage_4_spv_formed': ['expression_of_interest', 'ticket_blocking_paid', 'due_diligence', 'member_curation'],
      'stage_5_stage_1_paid': ['expression_of_interest', 'ticket_blocking_paid', 'due_diligence', 'member_curation', 'spv_formed'],
      'stage_6_asset_purchased': ['expression_of_interest', 'ticket_blocking_paid', 'due_diligence', 'member_curation', 'spv_formed', 'stage1_processed'],
      'stage_7_asset_deployed': ['expression_of_interest', 'ticket_blocking_paid', 'due_diligence', 'member_curation', 'spv_formed', 'stage1_processed', 'asset_purchased']
    };
    
    // If stage is provided, use it to determine completed stages
    if (stage && stageMapping[stage]) {
      stageMapping[stage].forEach(s => completed.add(s));
    } else {
      // Fallback: if no stage or invalid stage, just mark first stage as complete
      completed.add('expression_of_interest');
    }
    
    // Special case: if status is deployed/active, mark all stages complete
    if (status === 'deployed' || status === 'active') {
      stages.forEach(s => completed.add(s.id));
    }
    
    return completed;
  };

  const completedStages = getCompletedStages(currentStatus, stage);

  return (
    <div className="w-full">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Process Progress</h4>
      
      {/* Desktop View - Single Line or Two Line based on layout prop */}
      <div className="hidden md:block">
        {layout === "single-line" ? (
          /* Single Line Layout - Full Width */
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 z-0">
              <div 
                className="h-full bg-green-500 transition-all duration-500"
                style={{ 
                  width: `${(completedStages.size / stages.length) * 100}%` 
                }}
              />
            </div>
            
            {stages.map((stageItem, index) => {
              const isCompleted = completedStages.has(stageItem.id);
              
              return (
                <div key={stageItem.id} className="flex flex-col items-center relative z-10">
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    {isCompleted ? <Check size={16} /> : <span className="text-xs font-bold">{index + 1}</span>}
                  </div>
                  <div className="mt-2 text-center max-w-20">
                    <p className="text-xs font-medium text-gray-700 leading-tight whitespace-pre-line">
                      {stageItem.short}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Two Line Layout */
          <>
            {/* First Row */}
            <div className="flex items-center justify-between relative mb-8">
              {/* Progress Line for first row */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 z-0">
                <div 
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ 
                    width: `${(Math.min(completedStages.size, 4) / 4) * 100}%` 
                  }}
                />
              </div>
              
              {stages.slice(0, 4).map((stageItem, index) => {
                const isCompleted = completedStages.has(stageItem.id);
                
                return (
                  <div key={stageItem.id} className="flex flex-col items-center relative z-10">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}>
                      {isCompleted ? <Check size={16} /> : <span className="text-xs font-bold">{index + 1}</span>}
                    </div>
                    <div className="mt-2 text-center max-w-20">
                      <p className="text-xs font-medium text-gray-700 leading-tight whitespace-pre-line">
                        {stageItem.short}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Second Row */}
            <div className="flex items-center justify-between relative">
              {/* Progress Line for second row */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 z-0">
                <div 
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ 
                    width: `${(Math.max(0, completedStages.size - 4) / 4) * 100}%` 
                  }}
                />
              </div>
              
              {stages.slice(4, 8).map((stageItem, index) => {
                const isCompleted = completedStages.has(stageItem.id);
                
                return (
                  <div key={stageItem.id} className="flex flex-col items-center relative z-10">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}>
                      {isCompleted ? <Check size={16} /> : <span className="text-xs font-bold">{index + 5}</span>}
                    </div>
                    <div className="mt-2 text-center max-w-20">
                      <p className="text-xs font-medium text-gray-700 leading-tight whitespace-pre-line">
                        {stageItem.short}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Mobile View - Vertical */}
      <div className="md:hidden space-y-3">
        {stages.map((stageItem, index) => {
          const isCompleted = completedStages.has(stageItem.id);
          const isLast = index === stages.length - 1;
          
          return (
            <div key={stageItem.id} className="flex items-start relative">
              {!isLast && (
                <div className={`absolute left-4 top-8 w-0.5 h-6 ${
                  completedStages.has(stages[index + 1]?.id) ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
              
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                isCompleted 
                  ? 'bg-green-500 border-green-500 text-white' 
                  : 'bg-white border-gray-300 text-gray-400'
              }`}>
                {isCompleted ? <Check size={16} /> : <span className="text-xs font-bold">{index + 1}</span>}
              </div>
              
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${isCompleted ? 'text-green-700' : 'text-gray-600'}`}>
                  {stageItem.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessTracker;